import { ComboboxOption, ComboboxOptionText } from '@reach/combobox';
import React__default, { useMemo, Children, useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Box } from '../layout/Box.js';
import { Text } from '../text/Text.js';
import { SELECT_OPTION_NO_VALUE } from './DEPRECATED_commonSelect.js';
import { onChangeFieldWrapper } from './Field.js';

const MAX_COMBOBOX_SEARCH_RESULTS = 50;
const StyledComboboxOption = styled(ComboboxOption)`
  &[data-reach-combobox-option] {
    padding: 1rem;
    justify-content: center;
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
  }

  &[data-reach-combobox-option][aria-selected='true'] {
    background: ${COLOR_PALETTE.slateGrey5};
    color: inherit;
  }

  /* the string portions that match what the user has typed */
  & [data-user-value] {
    font-weight: inherit;
  }
  /* the string portions are suggested */
  & [data-suggested-value] {
    font-weight: 300;
  }
`;
const StyledComboboxOptionText = styled(ComboboxOptionText)``;
styled(Box)`
  height: 2.5rem;
  padding: 0 1rem;
  justify-content: center;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: ${COLOR_PALETTE.slateGrey40};
`;
/**
 * Select Input Option.
 * May accept either a simple label or component as a child.
 *
 * @example
 * <ComboboxOption label="Chicken 🐔" value="chicken" />
 */ const DEPRECATED_ComboboxOption = ({ label , value , testId ="X" , children , ...props })=>{
    // the reach combobox only registers on a 'value' - because of this, we use the label
    // as the searchable value, then map back to the actual form value when needed
    return React__default.createElement(StyledComboboxOption, {
        "data-hpy-web-combobox-option": true,
        ...props,
        ...setTestId("ComboboxOption-" + testId),
        label: value,
        value: label
    }, !!children ? children : React__default.createElement(Text, {
        weight: "semibold"
    }, React__default.createElement(StyledComboboxOptionText, null)));
};
DEPRECATED_ComboboxOption.dataId = "data-hpy-web-combobox-option";
DEPRECATED_ComboboxOption.displayName = "ComboboxOption";
/**
 * Combobox field helpers
 */ const useSearchComboboxOptions = (children, field, setValue, setTouched, onFieldChange, /**
 * Reset the search term to empty string when the combobox is initialised, blurred or an option is selected.
 * The corresponding field's value will not be cleared when the search text is cleared.
 */ resetSearchTermOnChange)=>{
    // create a dictionary of possible value/labels.
    // this is needed as the standard combobox uses a single value for search and selection. This doesn't work
    // for when the value in formik will need to be an ID or something similar with a descriptive label.
    const { optionLabels , optionValues  } = useMemo(()=>{
        const labels = {};
        const values = {};
        Children.forEach(children, (child)=>{
            if (child?.props?.label !== undefined) {
                values[child.props.label] = child.props.value;
                labels[child.props.value] = child.props.label;
            }
        });
        return {
            optionLabels: labels,
            optionValues: values
        };
    }, [
        children
    ]);
    // get selected label from a value
    const getSelectedLabel = useCallback((value)=>optionLabels[value] || "", [
        optionLabels
    ]);
    const getSelectedValue = useCallback((label)=>optionValues[label] || "", [
        optionValues
    ]);
    // currently selected label
    const selectedLabel = useMemo(()=>getSelectedLabel(field.value), [
        field.value,
        getSelectedLabel
    ]);
    const [searchTerm, setSearchTerm] = useState(resetSearchTermOnChange ? "" : selectedLabel);
    // do a search through provided child values
    const { filteredChildren , searchTotalCount  } = useMemo(()=>{
        const comboChildren = [];
        Children.forEach(children, (child)=>{
            if (!searchTerm || searchTerm === "" || child?.props.label?.toLocaleLowerCase()?.includes(searchTerm?.toLocaleLowerCase())) {
                comboChildren.push(child);
            }
        });
        // TODO: optimise search max results
        return {
            filteredChildren: comboChildren.slice(0, MAX_COMBOBOX_SEARCH_RESULTS),
            searchTotalCount: comboChildren.length
        };
    }, [
        children,
        searchTerm
    ]);
    // wrap the onChange to perform side effects if needed
    const handleChange = useCallback((label)=>{
        const updatedValue = optionValues[label] === SELECT_OPTION_NO_VALUE ? "" : getSelectedValue(label);
        setSearchTerm(resetSearchTermOnChange ? "" : label);
        !!onFieldChange ? onChangeFieldWrapper(onFieldChange, setValue)(updatedValue) : setValue(updatedValue);
    }, [
        getSelectedValue,
        onFieldChange,
        setValue,
        optionValues,
        resetSearchTermOnChange
    ]);
    // bindings for the search input
    const handleSearchTermChange = useCallback((event)=>{
        setSearchTerm(event.target.value);
    }, []);
    // when clicking outside the `Combobox` touch the field and ensure that a valid value is set
    const handleBlur = useCallback((e)=>{
        setTouched(true);
        if (e.relatedTarget?.hasAttribute("data-hpy-web-combobox-option")) {
            // Click is against an option, don't override the search term
            return;
        }
        if (resetSearchTermOnChange) {
            setSearchTerm("");
        } else {
            const selectedLabel = getSelectedLabel(field.value);
            !!selectedLabel && setSearchTerm(selectedLabel);
        }
    }, [
        field.value,
        getSelectedLabel,
        setTouched,
        resetSearchTermOnChange
    ]);
    // reset the current value if current value is completely removed from search
    useEffect(()=>{
        if (!resetSearchTermOnChange && searchTerm === "" && field.value !== "") {
            setValue("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchTerm
    ]);
    useEffect(()=>{
        if (!field.value) {
            setSearchTerm("");
        }
    }, [
        field.value
    ]);
    return {
        filteredChildren,
        handleSearchTermChange,
        handleBlur,
        searchTotalCount,
        searchTerm,
        handleChange,
        selectedLabel,
        setSearchTerm
    };
};

export { DEPRECATED_ComboboxOption, useSearchComboboxOptions };
